'use client';

/**
 * FinalCTA — shared closing block used across V3 pages (home, feature
 * pages, vertical pages). Renders the canonical V3 conversion section:
 * dark gradient background, centered halo in the page accent color,
 * two-clause headline (white + accent), subhead, and a primary +
 * secondary CTA pair.
 *
 * Accepts a typed copy object so each page can keep its own strings
 * and link destinations without re-implementing the JSX. The accent
 * controls both the halo color and the second-clause headline color.
 */

import CtaLink from '@/components/common/CtaLink';
import React from 'react';

export type FinalCTAAccent = '#22D3EE' | '#A78BFA' | '#FFB74D' | '#7FF4FF';

export type FinalCTAAction = {
  label: string;
  href: string;
  ariaLabel?: string;
};

export type FinalCTACopy = {
  headlineA: string;
  /** Second clause, rendered in the accent color. */
  headlineB: string;
  subhead: string;
  /** Optional when `primaryAction` is provided — that node replaces the primary CTA. */
  ctaPrimary?: FinalCTAAction;
  ctaSecondary: FinalCTAAction;
};

type Props = {
  accent: FinalCTAAccent;
  copy: FinalCTACopy;
  /** Optional landmark label for the section. */
  ariaLabel?: string;
  /** Container max-width override. Defaults to 720px. */
  maxWidth?: number;
  /**
   * Override the default primary `<CtaLink>` with a custom node — used when the
   * primary CTA opens a modal, runs a handler, etc. (e.g. /our-teams uses
   * PersonalizedLeadTrigger). When provided, `copy.ctaPrimary` is ignored.
   */
  primaryAction?: React.ReactNode;
};

/** rgba halo per accent — kept in sync with VerticalHero halo map. */
const HALO_RGB: Record<FinalCTAAccent, string> = {
  '#22D3EE': '34,211,238',
  '#A78BFA': '167,139,250',
  '#FFB74D': '255,183,77',
  '#7FF4FF': '127,244,255',
};

const FinalCTA = ({ accent, copy, ariaLabel, maxWidth = 720, primaryAction }: Props) => {
  const halo = `radial-gradient(circle, rgba(${HALO_RGB[accent]},0.18) 0%, transparent 60%)`;

  return (
    <section
      aria-label={ariaLabel}
      className="relative overflow-hidden px-6 py-28 lg:py-32 text-center"
      style={{
        background: 'linear-gradient(180deg, #0E1130 0%, #0A0E27 100%)',
        color: '#F5F7FA',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 600,
          height: 600,
          transform: 'translate(-50%, -50%)',
          background: halo,
          pointerEvents: 'none',
        }}
      />
      <div className="relative mx-auto" style={{ maxWidth }}>
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(32px, 4.4vw, 56px)',
            lineHeight: 1.08,
            letterSpacing: '-0.02em',
            margin: 0,
          }}
        >
          {copy.headlineA}
          {copy.headlineB ? (
            <>
              {' '}
              <span style={{ color: accent }}>{copy.headlineB}</span>
            </>
          ) : null}
        </h2>
        <p style={{ color: '#B4C1D8', fontSize: 20, margin: '18px 0 32px' }}>{copy.subhead}</p>
        <div className="flex flex-col sm:flex-row sm:justify-center gap-3 sm:gap-4">
          {primaryAction ??
            (copy.ctaPrimary && (
              <CtaLink href={copy.ctaPrimary.href} className="sum-cta-primary" aria-label={copy.ctaPrimary.ariaLabel}>
                {copy.ctaPrimary.label}
              </CtaLink>
            ))}
          <CtaLink href={copy.ctaSecondary.href} className="sum-cta-ghost" aria-label={copy.ctaSecondary.ariaLabel}>
            {copy.ctaSecondary.label}
          </CtaLink>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
